'use client';

import { useState, useCallback, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Link as LinkIcon } from 'lucide-react';

interface MarkdownContentProps {
  content: string;
  className?: string;
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.left = '-999999px';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [text]);

  return (
    <button
      onClick={handleCopy}
      className="copy-btn absolute top-2 right-2 z-10 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-gray-800/90 text-white hover:bg-gray-700 transition-all shadow-lg border border-gray-600/50 opacity-0 group-hover:opacity-100"
      aria-label={copied ? 'Copied!' : 'Copy to clipboard'}
    >
      {copied ? (
        <>
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          Copied!
        </>
      ) : (
        <>
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
          Copy
        </>
      )}
    </button>
  );
}

function CopyableCodeBlock({ children, text }: { children: React.ReactNode; text: string }) {
  const [showOverlay, setShowOverlay] = useState(false);
  const mouseDownPos = useRef<{ x: number; y: number } | null>(null);

  const copyText = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.left = '-999999px';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setShowOverlay(true);
    setTimeout(() => setShowOverlay(false), 1500);
  }, [text]);

  return (
    <div
      className="relative group"
      onMouseDown={(e) => { mouseDownPos.current = { x: e.clientX, y: e.clientY }; }}
      onClick={(e) => {
        if ((e.target as HTMLElement).closest('.copy-btn')) return;
        if (mouseDownPos.current) {
          const dx = Math.abs(e.clientX - mouseDownPos.current.x);
          const dy = Math.abs(e.clientY - mouseDownPos.current.y);
          if (dx > 5 || dy > 5) return;
        }
        const sel = window.getSelection();
        if (sel && sel.toString().length > 0) return;
        copyText();
      }}
    >
      {children}
      <CopyButton text={text} />
      {showOverlay && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/60 rounded-lg text-white font-medium gap-2 pointer-events-none">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          Copied!
        </div>
      )}
    </div>
  );
}

function CopyableInlineCode({ children }: { children: React.ReactNode }) {
  const [copied, setCopied] = useState(false);
  const text = String(children);

  const handleClick = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.left = '-999999px';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }, [text]);

  return (
    <code
      onClick={handleClick}
      className={`cursor-pointer px-1.5 py-0.5 rounded text-sm bg-gray-100 border border-gray-200 hover:bg-blue-50 hover:border-blue-200 transition-colors ${copied ? 'bg-green-50 border-green-200' : ''}`}
      title={copied ? 'Copied!' : 'Click to copy'}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleClick(); }
      }}
    >
      {copied ? (
        <span className="inline-flex items-center gap-1">
          <svg className="w-3 h-3 inline text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          {children}
        </span>
      ) : children}
    </code>
  );
}

function getTextContent(children: React.ReactNode): string {
  if (typeof children === 'string') return children;
  if (typeof children === 'number') return String(children);
  if (!children) return '';
  if (Array.isArray(children)) return children.map(getTextContent).join('');
  if (typeof children === 'object' && 'props' in children) {
    return getTextContent((children as React.ReactElement<{ children?: React.ReactNode }>).props?.children);
  }
  return '';
}

function slugify(text: string): string {
  return text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

function HeadingWithAnchor({ level, children }: { level: 2 | 3 | 4; children: React.ReactNode }) {
  const [copied, setCopied] = useState(false);
  const text = getTextContent(children);
  const id = slugify(text);
  const Tag = `h${level}` as 'h2' | 'h3' | 'h4';

  const handleCopyLink = useCallback(async (e: React.MouseEvent) => {
    e.preventDefault();
    const url = `${window.location.origin}${window.location.pathname}#${id}`;
    try {
      await navigator.clipboard.writeText(url);
    } catch { /* ignore */ }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [id]);

  return (
    <Tag id={id} className="group relative">
      <a
        href={`#${id}`}
        onClick={handleCopyLink}
        className="absolute -left-6 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity text-gray-400 hover:text-blue-600"
        aria-label={`Link to ${text}`}
        title={copied ? 'Link copied!' : 'Click to copy link'}
      >
        <LinkIcon size={level === 2 ? 18 : 16} />
      </a>
      {children}
      {copied && (
        <span className="ml-2 text-xs font-normal text-green-600 bg-green-50 px-2 py-0.5 rounded">
          Link copied!
        </span>
      )}
    </Tag>
  );
}

function stripFrontmatter(content: string): string {
  return content.replace(/^---\s*\n[\s\S]*?\n---\s*\n?/, '').trim();
}

export default function MarkdownContent({ content, className = 'prose prose-lg max-w-none' }: MarkdownContentProps) {
  const clean = stripFrontmatter(content);

  return (
    <div className={className}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          h1: ({ children }) => <HeadingWithAnchor level={2}>{children}</HeadingWithAnchor>,
          h2: ({ children }) => <HeadingWithAnchor level={2}>{children}</HeadingWithAnchor>,
          h3: ({ children }) => <HeadingWithAnchor level={3}>{children}</HeadingWithAnchor>,
          h4: ({ children }) => <HeadingWithAnchor level={4}>{children}</HeadingWithAnchor>,
          table: ({ children, ...props }) => (
            <div className="overflow-x-auto -mx-1 px-1" tabIndex={0}>
              <table {...props}>{children}</table>
            </div>
          ),
          img: ({ src, alt, title, ...props }) => {
            const srcStr = typeof src === 'string' ? src : '';
            const altText = alt || title || 'Image';
            // eslint-disable-next-line @next/next/no-img-element
            return <img src={srcStr} alt={altText} title={title} loading="lazy" {...props} />;
          },
          pre: ({ children, ...props }) => {
            const text = getTextContent(children);
            return (
              <CopyableCodeBlock text={text}>
                <pre {...props} tabIndex={0}>{children}</pre>
              </CopyableCodeBlock>
            );
          },
          code: ({ children, className, ...props }) => {
            const text = getTextContent(children);
            const isBlock = className?.startsWith('language-') || text.includes('\n');
            if (!isBlock) {
              return <CopyableInlineCode {...props}>{children}</CopyableInlineCode>;
            }
            return <code className={className} {...props}>{children}</code>;
          },
        }}
      >
        {clean}
      </ReactMarkdown>
    </div>
  );
}
